import { useCallback } from 'react';
import { Box } from '@chakra-ui/react';
import { Colors } from '@mercator-gcp/grout';
import ChatView from 'components/widgets/Chat/ChatView';
import FilterToolbarWidget from 'components/widgets/FilterToolbar';
import ClearAllFiltersButton from 'components/widgets/FilterToolbar/ClearAllFiltersButton';
import FilterToolbarItem from 'components/widgets/FilterToolbar/ToolbarItem';
import { isChatCreatedSavedSearch } from 'components/widgets/ProjectInsights/Toolbar/getSavedSearchEditorType';
import SelectExistingGroup from 'components/widgets/ProjectInsights/Toolbar/SelectExistingGroup';
import {
  CHAT_SIDEBAR_WIDTH,
  DEFAULT_SIDEBAR_WIDTH,
} from 'components/widgets/ProjectInsights/Toolbar/sidebarWidth';
import { useSelectedGroupConfig } from 'components/widgets/ProjectInsights/Toolbar/useSelectedGroupConfig';
import { useInvalidateGroupConfigurations } from 'hooks/useGroupConfig';
import { useHandleProjectGroupSelect } from 'hooks/useProjectMap';
import { useSavedSearchChatSession } from 'hooks/useSavedSearchChatSession';
import { useUser } from 'state/auth';
import { useDashboardGroupId } from 'state/dashboard';
import { isMapFilterVisibleState } from 'state/map';
import { useGroupIdQuery } from 'state/queryParams';
import { useSavedSearchQueryHash } from 'state/scoutChat';
import type { SavedSearchChange } from 'services/API/AIInteractionAPI';

const baseToolbarStyle = {
  width: '100%',
  height: '100%',
  left: 0,
  top: 0,
  bottom: 0,
  background: Colors.White,
  display: 'flex',
  flexDirection: 'column',
};

const chatBorderStyle = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  margin: 'padding_8',
  border: `1px solid ${Colors.ArmyGreen300}`,
  borderRadius: '8px',
};

const ProjectInsightsToolbar = () => {
  const { setGroupIdQuery } = useGroupIdQuery();
  const [, setGroupId] = useDashboardGroupId();
  const [isMapFilterVisible] = isMapFilterVisibleState();
  const selectedGroup = useSelectedGroupConfig();
  const isChatEditor = isChatCreatedSavedSearch(selectedGroup);
  const { session } = useSavedSearchChatSession(selectedGroup?.adk_session_id);
  const [, setSavedSearchQueryHash] = useSavedSearchQueryHash();
  const { user } = useUser();
  const { invalidateGroupConfigurations } = useInvalidateGroupConfigurations();

  const handleSavedSearchChange = useCallback(
    (changes: SavedSearchChange[]) => {
      setSavedSearchQueryHash((prev) => {
        const next = { ...prev };
        changes.forEach(({ adk_query_id, query_md5 }) => {
          next[adk_query_id] = query_md5;
        });

        return next;
      });

      // The saved-search list (used to resolve the chat's data-part link back
      // to a group id/name) is a separate query from the chat itself, so it
      // needs its own explicit invalidation - same pattern as every other
      // group-mutating flow (SelectExistingGroup, DeleteProjectGroupModal, etc).
      if (user?.id) {
        invalidateGroupConfigurations(String(user.id));
      }
    },
    [setSavedSearchQueryHash, user?.id, invalidateGroupConfigurations],
  );

  /* Rodrigo - Feb 22, 2023
   * This hook would ideally be called from inside the toolbar.
   * We currently have an issue where the toolbar reloads unnecessarily, and it
   * overwrites the effects of this hook.
   * I am moving it into this file for now because it is outside of the
   * re-render issue.
   */
  useHandleProjectGroupSelect();

  const onLocationChangeCallback = useCallback(() => {
    // Reset group selection when city changes
    setGroupIdQuery('');
    setGroupId('');
  }, [setGroupId, setGroupIdQuery]);

  if (!isMapFilterVisible) {
    return null;
  }

  const toolbarStyle = {
    ...baseToolbarStyle,
    maxWidth: isChatEditor ? CHAT_SIDEBAR_WIDTH : DEFAULT_SIDEBAR_WIDTH,
  };

  // Saved searches created via a Scout chat prompt can't have their filters
  // edited directly - all edits go through the chat instead. The chat's
  // query_md5 changes get recorded in savedSearchQueryHashAtom, which
  // useScoutbotSavedSearchQueries keys its refetch off of.
  if (isChatEditor) {
    return (
      <Box sx={toolbarStyle}>
        <FilterToolbarItem>
          <SelectExistingGroup />
        </FilterToolbarItem>
        {session && (
          <Box sx={chatBorderStyle}>
            <ChatView
              chat={session}
              showFooter={false}
              inputPlaceholder="Ask Scout to refine this search..."
              onSavedSearchChange={handleSavedSearchChange}
            />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={toolbarStyle}>
      <FilterToolbarWidget
        headerElement={
          <FilterToolbarItem>
            <SelectExistingGroup />
          </FilterToolbarItem>
        }
        footerElement={<ClearAllFiltersButton />}
        onLocationChangeCallback={onLocationChangeCallback}
      />
    </Box>
  );
};

export default ProjectInsightsToolbar;
